export const stripHtmlTags = (html) => {
  if (!html || typeof html !== "string") return "";

  let text = html.replace(/<[^>]+>/g, "");

  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  text = text.replace(/\s+/g, " ").trim();

  return text;
};

export const parseDeltaToHtml = (deltaContent) => {
  try {
    const delta =
      typeof deltaContent === "string"
        ? JSON.parse(deltaContent)
        : deltaContent;

    if (!Array.isArray(delta)) {
      return deltaContent;
    }

    let html = "";

    delta.forEach((op) => {
      if (!op.insert) return;

      const attributes = op.attributes || {};

      if (typeof op.insert === "object") {
        if (op.insert.image) {
          const width = attributes.style?.match(/width:\s*([\d.]+)/)?.[1] || "";
          const height =
            attributes.style?.match(/height:\s*([\d.]+)/)?.[1] || "";

          const styleAttr =
            width && height
              ? `style="max-width: 100%; height: auto;"`
              : `style="max-width: 100%; height: auto;"`;

          html += `<img src="${op.insert.image}" alt="content image" ${styleAttr} />`;
        } else if (op.insert.video) {
          html += `<video src="${op.insert.video}" controls style="max-width: 100%;"></video>`;
        }
        return;
      }

      let text = op.insert;

      if (text === "\n") {
        html += "<br />";
        return;
      }

      if (attributes.bold) {
        text = `<strong>${text}</strong>`;
      }
      if (attributes.italic) {
        text = `<em>${text}</em>`;
      }
      if (attributes.underline) {
        text = `<u>${text}</u>`;
      }
      if (attributes.strike) {
        text = `<s>${text}</s>`;
      }

      if (attributes.link) {
        text = `<a href="${attributes.link}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline;">${text}</a>`;
      }

      if (attributes.color) {
        text = `<span style="color: ${attributes.color}">${text}</span>`;
      }
      if (attributes.background) {
        text = `<span style="background-color: ${attributes.background}">${text}</span>`;
      }

      if (attributes.header) {
        const level = attributes.header;
        text = `<h${level}>${text}</h${level}>`;
      }

      if (attributes.list) {
        const listType = attributes.list === "ordered" ? "ol" : "ul";
        text = `<${listType}><li>${text}</li></${listType}>`;
      }

      html += text;
    });

    return html;
  } catch (error) {
    console.error("Error parsing delta content:", error);
    return deltaContent;
  }
};

export const parseDeltaToPlainText = (deltaContent) => {
  if (!deltaContent) return "";

  try {
    const delta =
      typeof deltaContent === "string"
        ? JSON.parse(deltaContent)
        : deltaContent;

    if (!Array.isArray(delta)) {
      return stripHtmlTags(deltaContent);
    }

    let text = "";

    delta.forEach((op) => {
      if (op.insert) {
        text += op.insert;
      }
    });

    return text.replace(/\n/g, " ").trim();
  } catch (error) {
    return stripHtmlTags(deltaContent);
  }
};

export const isDeltaFormat = (content) => {
  if (!content) return false;

  try {
    const parsed = typeof content === "string" ? JSON.parse(content) : content;
    return (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed[0].hasOwnProperty("insert")
    );
  } catch {
    return false;
  }
};

export const isHtmlFormat = (content) => {
  if (!content || typeof content !== "string") return false;

  const htmlTagPattern = /<[^>]+>/;
  return htmlTagPattern.test(content);
};

export const convertNewlinesToBr = (text) => {
  if (!text || typeof text !== "string") return "";
  return text.replace(/\n/g, "<br />");
};

export const parseContent = (content) => {
  if (!content) return "";

  if (isDeltaFormat(content)) {
    return parseDeltaToHtml(content);
  }

  if (isHtmlFormat(content)) {
    return content;
  }

  return convertNewlinesToBr(content);
};

export const parseDeltaToHtmlWithLimit = (deltaContent, limit = 200) => {
  try {
    const delta =
      typeof deltaContent === "string"
        ? JSON.parse(deltaContent)
        : deltaContent;

    if (!Array.isArray(delta)) {
      return { html: deltaContent, isTruncated: false };
    }

    let html = "";
    let textLength = 0;
    let isTruncated = false;

    for (const op of delta) {
      if (!op.insert) continue;

      const attributes = op.attributes || {};

      if (typeof op.insert === "object") {
        if (textLength >= limit) {
          isTruncated = true;
          break;
        }

        if (op.insert.image) {
          html += `<img src="${op.insert.image}" alt="content image" style="max-width: 100%; height: auto;" />`;
          textLength += 1;
        } else if (op.insert.video) {
          html += `<video src="${op.insert.video}" controls style="max-width: 100%;"></video>`;
          textLength += 1;
        }
        continue;
      }

      let text = op.insert;

      const remainingLength = limit - textLength;
      if (remainingLength <= 0) {
        isTruncated = true;
        break;
      }

      if (text === "\n") {
        html += "<br />";
        continue;
      }

      const actualTextLength = text.length;

      if (textLength + actualTextLength > limit) {
        text = text.substring(0, remainingLength);
        isTruncated = true;
      }

      textLength += text.length;

      if (attributes.bold) {
        text = `<strong>${text}</strong>`;
      }
      if (attributes.italic) {
        text = `<em>${text}</em>`;
      }
      if (attributes.underline) {
        text = `<u>${text}</u>`;
      }
      if (attributes.strike) {
        text = `<s>${text}</s>`;
      }

      if (attributes.link) {
        text = `<a href="${attributes.link}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline;">${text}</a>`;
      }

      if (attributes.color) {
        text = `<span style="color: ${attributes.color}">${text}</span>`;
      }
      if (attributes.background) {
        text = `<span style="background-color: ${attributes.background}">${text}</span>`;
      }

      if (attributes.header) {
        const level = attributes.header;
        text = `<h${level}>${text}</h${level}>`;
      }

      if (attributes.list) {
        const listType = attributes.list === "ordered" ? "ol" : "ul";
        text = `<${listType}><li>${text}</li></${listType}>`;
      }

      html += text;

      if (isTruncated) {
        break;
      }
    }

    return { html, isTruncated };
  } catch (error) {
    console.error("Error parsing delta content with limit:", error);
    return { html: deltaContent, isTruncated: false };
  }
};
